import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
  CartesianGrid, Legend, PieChart, Pie,
} from 'recharts';
import SourceViewerModal from './SourceViewerModal';

const COLORS = [
  '#3b5bdb', '#0ca678', '#f59f00', '#ae3ec9', '#e8590c',
  '#1098ad', '#74b816', '#d6336c', '#4263eb', '#099268',
  '#e67700', '#862e9c',
];

const formatCurrency = (val) => {
  if (val == null) return '--';
  if (val >= 1000) return '$' + (val / 1000).toFixed(1) + 'k';
  return '$' + Number(val).toFixed(2);
};

const formatFullCurrency = (val) => {
  if (val == null) return '--';
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Filter spendOverTime / spendByVendorOverTime rows by date range
function filterMonthlyData(rows, fromMonth, toMonth) {
  if (!fromMonth && !toMonth) return rows;
  return rows.filter((r) => {
    if (fromMonth && r.month < fromMonth) return false;
    if (toMonth && r.month > toMonth) return false;
    return true;
  });
}

const SpendAnalyticsView = ({ loaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [pdfModal, setPdfModal] = useState(null); // { invoiceId, item, url, loading }

  const handleViewInvoicePdf = async (documentId, rawDescription) => {
    if (!documentId) return;
    const item = { description: rawDescription || '', document_id: documentId };
    setPdfModal({ invoiceId: documentId, item, url: null, loading: true });
    try {
      // Try bills endpoint first, fall back to invoices-v2 (enrichment data may reference either)
      let res = await apiFetch(`/api/bills/${documentId}/download`);
      if (!res.ok) {
        res = await apiFetch(`/api/invoices-v2/${documentId}/download`);
      }
      if (!res.ok) throw new Error('Failed to get download URL');
      const data = await res.json();
      const enrichedItem = {
        ...item,
        invoices: { invoice_number: data.bill_number },
        vendor_name: data.vendor_name,
        bill_date: data.bill_date,
      };
      setPdfModal((prev) => prev ? { ...prev, item: enrichedItem, url: data.downloadUrl, loading: false } : null);
    } catch {
      setPdfModal(null);
    }
  };

  // Filters
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [vendorFilter, setVendorFilter] = useState(''); // '' = all

  useEffect(() => {
    apiFetch('/api/invoices-v2/spend-analytics')
      .then((res) => res.json())
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  // Derived data — must be above early returns (Rules of Hooks)
  const categoryBreakdown = data?.categoryBreakdown || [];
  const spendOverTime = data?.spendOverTime || [];
  const spendByVendorOverTime = data?.spendByVendorOverTime || [];
  const topVendors = data?.topVendors || [];
  const topProducts = data?.topProducts || [];
  const priceTrends = data?.priceTrends || [];
  const summary = data?.summary || {};
  const allVendorNames = topVendors.slice(0, 8).map((v) => v.name);

  const filteredSpendOverTime = filterMonthlyData(spendOverTime, fromMonth, toMonth);
  const filteredSpendByVendorOverTime = filterMonthlyData(spendByVendorOverTime, fromMonth, toMonth);

  const filteredCategoryBreakdown = useMemo(() => {
    if (!fromMonth && !toMonth && !vendorFilter) return categoryBreakdown;
    const totals = {};
    filteredSpendOverTime.forEach((row) => {
      Object.entries(row).forEach(([k, v]) => {
        if (k === 'month') return;
        totals[k] = (totals[k] || 0) + v;
      });
    });
    const totalSpend = Object.values(totals).reduce((s, v) => s + v, 0);
    return categoryBreakdown
      .filter((c) => totals[c.category] != null)
      .map((c) => {
        const filteredTotal = totals[c.category];
        const scale = c.total > 0 ? filteredTotal / c.total : 0;
        return {
          ...c,
          total: Math.round(filteredTotal * 100) / 100,
          percentage: totalSpend > 0 ? Math.round((filteredTotal / totalSpend) * 10000) / 100 : 0,
          subcategories: c.subcategories.map((sub) => ({
            ...sub,
            total: Math.round(sub.total * scale * 100) / 100,
          })),
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [filteredSpendOverTime, categoryBreakdown, fromMonth, toMonth, vendorFilter]);

  const filteredTopVendors = useMemo(() => {
    if (!fromMonth && !toMonth) return topVendors;
    const totals = {};
    filteredSpendByVendorOverTime.forEach((row) => {
      Object.entries(row).forEach(([k, v]) => {
        if (k === 'month') return;
        totals[k] = (totals[k] || 0) + v;
      });
    });
    return topVendors
      .map((v) => ({ ...v, total: Math.round((totals[v.name] || 0) * 100) / 100 }))
      .filter((v) => v.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredSpendByVendorOverTime, topVendors, fromMonth, toMonth]);

  // Collapse long tail into "Other", separate out "Uncategorized"
  const MAX_VISIBLE_CATEGORIES = 8;
  const { displayCategories, uncategorizedEntry, hasOtherBucket } = useMemo(() => {
    const uncategorized = filteredCategoryBreakdown.find((c) => c.category?.toLowerCase() === 'uncategorized');
    const categorized = filteredCategoryBreakdown.filter((c) => c.category?.toLowerCase() !== 'uncategorized');

    if (showAllCategories) {
      return { displayCategories: categorized, uncategorizedEntry: uncategorized || null, hasOtherBucket: false };
    }

    const visible = categorized.slice(0, MAX_VISIBLE_CATEGORIES);
    const rest = categorized.slice(MAX_VISIBLE_CATEGORIES);

    if (rest.length > 0) {
      const otherTotal = rest.reduce((sum, c) => sum + c.total, 0);
      const totalSpend = filteredCategoryBreakdown.reduce((sum, c) => sum + c.total, 0);
      visible.push({
        category: `Other (${rest.length})`,
        total: Math.round(otherTotal * 100) / 100,
        percentage: totalSpend > 0 ? Math.round((otherTotal / totalSpend) * 10000) / 100 : 0,
        subcategories: rest.map((c) => ({ name: c.category, total: c.total })),
        _isOther: true,
      });
    }

    return { displayCategories: visible, uncategorizedEntry: uncategorized || null, hasOtherBucket: rest.length > 0 };
  }, [filteredCategoryBreakdown, showAllCategories]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    const PRIMARY_COLOR = '#1d4ed8';
    filteredCategoryBreakdown.forEach((c, i) => {
      if (c.category?.toLowerCase() === 'uncategorized') return;
      const opacity = 1 - (i * 0.08);
      map[c.category] = i === 0 ? PRIMARY_COLOR : `rgba(29, 78, 216, ${Math.max(0.25, opacity)})`;
    });
    displayCategories.forEach((c) => {
      if (c._isOther) map[c.category] = '#d4d4d8';
    });
    return map;
  }, [filteredCategoryBreakdown, displayCategories]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.summary || data.summary.itemCount === 0) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-light text-zinc-900 mb-1">Spend Analytics</h1>
          <p className="text-sm text-zinc-400">AI-powered spend analysis across all enriched invoices</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <p className="text-sm text-zinc-400">No enriched data yet. Upload and process invoices to see spend analytics.</p>
        </div>
      </div>
    );
  }

  const filteredTotalSpend = filteredCategoryBreakdown.reduce((s, c) => s + c.total, 0);

  // Vendor names for the chart series — filter to selected vendor if set
  const vendorNames = vendorFilter
    ? allVendorNames.filter((v) => v === vendorFilter)
    : allVendorNames;

  const vendorColorMap = {};
  allVendorNames.forEach((v, i) => { vendorColorMap[v] = COLORS[i % COLORS.length]; });

  const topCategory = filteredCategoryBreakdown[0] || null;
  const displayedCategory = filteredCategoryBreakdown.find((c) => c.category === expandedCategory) || topCategory;

  // Available months for the date pickers
  const allMonths = spendOverTime.map((r) => r.month);
  const isFiltered = fromMonth || toMonth || vendorFilter;

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <div
        className="mb-4 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-light text-zinc-900 mb-1">Spend Analytics</h1>
            <p className="text-sm text-zinc-400">AI-powered spend analysis across all enriched invoices</p>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="text-xs border border-zinc-200 rounded-md px-2.5 py-1.5 text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
            >
              <option value="">All Vendors</option>
              {allVendorNames.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>

            <select
              value={fromMonth}
              onChange={(e) => setFromMonth(e.target.value)}
              className="text-xs border border-zinc-200 rounded-md px-2.5 py-1.5 text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
            >
              <option value="">From</option>
              {allMonths.map((m) => {
                const [y, mo] = m.split('-');
                return <option key={m} value={m}>{new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</option>;
              })}
            </select>

            <select
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              className="text-xs border border-zinc-200 rounded-md px-2.5 py-1.5 text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
            >
              <option value="">To</option>
              {allMonths.map((m) => {
                const [y, mo] = m.split('-');
                return <option key={m} value={m}>{new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</option>;
              })}
            </select>

            {isFiltered && (
              <button
                onClick={() => { setFromMonth(''); setToMonth(''); setVendorFilter(''); }}
                className="text-xs text-zinc-400 hover:text-zinc-900 transition-colors px-1"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Section 01: Summary */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '350ms',
        }}
      >
        <SectionHeader step="01" title="Overview" description="High-level spend metrics for the current filter." />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-3">
          <SummaryCard label="Total Spend" value={formatFullCurrency(isFiltered ? filteredTotalSpend : summary.totalSpend)} dimmed={isFiltered} />
          <SummaryCard label="Invoices" value={summary.invoiceCount} />
          <SummaryCard label="Line Items" value={summary.itemCount.toLocaleString()} />
          <SummaryCard label="Categories" value={isFiltered ? filteredCategoryBreakdown.length : summary.categoryCount} dimmed={isFiltered} />
          <SummaryCard label="Vendors" value={isFiltered ? filteredTopVendors.length : summary.vendorCount} dimmed={isFiltered} />
        </div>
      </div>

      {/* Section 02: Category spend bar chart */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '400ms',
        }}
      >
        <SectionHeader step="02" title="Where Spend Is Concentrated" description="Category and vendor breakdowns to identify the biggest financial levers." />

        {/* Category chart + detail panel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Spend by Category</h2>
              {showAllCategories && (
                <button
                  onClick={() => setShowAllCategories(false)}
                  className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  Show less
                </button>
              )}
            </div>
            <div className={showAllCategories ? 'overflow-y-auto' : ''} style={showAllCategories ? { maxHeight: 380 } : undefined}>
              <div style={{ height: Math.max(220, displayCategories.length * 42) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={displayCategories} layout="vertical" margin={{ left: 0, right: 80, top: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="category" width={160} tick={{ fontSize: 12, fill: '#3f3f46' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      formatter={(value, _name, props) => {
                        const pct = props?.payload?.percentage;
                        return [
                          `${formatFullCurrency(value)}${pct != null ? ` (${pct.toFixed(1)}%)` : ''}`,
                          'Spend',
                        ];
                      }}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12 }}
                    />
                    <Bar
                      dataKey="total"
                      radius={[0, 5, 5, 0]}
                      cursor="pointer"
                      onClick={(entry) => {
                        if (entry._isOther) {
                          setShowAllCategories(true);
                        } else {
                          setExpandedCategory(entry.category);
                        }
                      }}
                    >
                      <LabelList
                        dataKey="total"
                        position="right"
                        content={({ x, y, width, height, value, index }) => {
                          const entry = displayCategories[index];
                          const pct = entry?.percentage;
                          return (
                            <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11} fill="#52525b" fontWeight={500}>
                              {formatCurrency(value)} <tspan fill="#a1a1aa">{pct != null ? `${pct.toFixed(0)}%` : ''}</tspan>
                            </text>
                          );
                        }}
                      />
                      {displayCategories.map((entry) => (
                        <Cell
                          key={entry.category}
                          fill={categoryColorMap[entry.category] || '#d4d4d8'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <CategoryDetailPanel
            category={displayedCategory}
            color={displayedCategory ? categoryColorMap[displayedCategory.category] : COLORS[0]}
          />
        </div>
        {uncategorizedEntry && uncategorizedEntry.total > 0 && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <svg className="w-4 h-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>
              <span className="font-medium">{formatFullCurrency(uncategorizedEntry.total)}</span> uncategorized
              ({uncategorizedEntry.percentage?.toFixed(1) || '0'}% of spend) — review these items to improve analytics accuracy.
            </span>
          </div>
        )}
      </div>

      {/* Section 03: Spend over time */}
      {filteredSpendOverTime.length > 0 && (
        <div
          className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(12px)',
            transitionDelay: '450ms',
          }}
        >
          <SpendOverTimeChart
            data={filteredSpendOverTime}
            formatCurrency={formatCurrency}
            formatFullCurrency={formatFullCurrency}
          />
        </div>
      )}

      {/* Section 04: Spend by vendor over time */}
      {filteredSpendByVendorOverTime?.length > 0 && vendorNames.length > 0 && (
        <div
          className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(12px)',
            transitionDelay: '475ms',
          }}
        >
          <VendorSpendPie
            data={filteredSpendByVendorOverTime}
            vendorNames={vendorNames}
            vendorColorMap={vendorColorMap}
            formatFullCurrency={formatFullCurrency}
          />
        </div>
      )}

      {/* Section 05: Top Vendors + Top Products */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '500ms',
        }}
      >
        <SectionHeader step="05" title="Concentration" description="Top vendors and products by total spend." />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <TopVendorsTable vendors={filteredTopVendors} totalSpend={filteredTotalSpend} />
          <TopProductsTable products={topProducts} />
        </div>
      </div>

      {/* PDF viewer */}
      {pdfModal && !pdfModal.loading && pdfModal.url && (
        <SourceViewerModal
          pdfUrl={pdfModal.url}
          item={pdfModal.item}
          allItems={[pdfModal.item]}
          onClose={() => setPdfModal(null)}
          onItemChange={() => {}}
        />
      )}

      {/* Section 06: Price Intelligence */}
      {priceTrends.length > 0 && (
        <div
          className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(12px)',
            transitionDelay: '550ms',
          }}
        >
          <SectionHeader step="06" title="Price Intelligence" description="Normalized unit cost drift — products appearing in 2+ months." />
          <div className="mt-3">
            <PriceDriftTable trends={priceTrends} onViewPdf={handleViewInvoicePdf} activeInvoiceId={pdfModal?.invoiceId} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Summary card ─── */
const SummaryCard = ({ label, value, dimmed }) => (
  <div className={`bg-white border rounded-lg px-4 py-3 transition-colors ${dimmed ? 'border-zinc-300' : 'border-zinc-200'}`}>
    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1">{label}</p>
    <p className={`text-xl font-light transition-colors ${dimmed ? 'text-zinc-500' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

/* ─── Category drill-down ─── */
const CategoryDetailPanel = ({ category, color }) => {
  if (!category) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-4">
        <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Category Detail</h2>
        <p className="text-sm text-zinc-500">Select a category to see where that spend is concentrated.</p>
      </div>
    );
  }
  const subcategories = (category.subcategories || []).slice(0, 7);
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
        <h2 className="text-sm font-medium text-zinc-900">{category.category}</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4">{formatFullCurrency(category.total)} total &bull; {category.percentage?.toFixed(1) || '0'}% of spend</p>
      <div className="space-y-2.5">
        {subcategories.map((sub) => {
          const pct = category.total > 0 ? (sub.total / category.total) * 100 : 0;
          return (
            <div key={sub.name} className="flex items-center gap-3">
              <span className="text-xs text-zinc-600 w-44 shrink-0 truncate">{sub.name}</span>
              <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color, opacity: 0.75 }} />
              </div>
              <span className="text-xs text-zinc-600 w-20 text-right shrink-0">{formatFullCurrency(sub.total)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Top vendors table ─── */
const TopVendorsTable = ({ vendors, totalSpend }) => (
  <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-zinc-100">
      <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Top Vendors</h2>
    </div>
    <div className="overflow-y-auto max-h-[400px]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100">
            <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Vendor</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Invoices</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Spend</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((v, i) => {
            const pct = totalSpend > 0 ? (v.total / totalSpend) * 100 : 0;
            return (
              <tr key={v.name} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-300 w-4">{i + 1}</span>
                    <span className="text-zinc-900">{v.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right text-zinc-500">{v.invoiceCount}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className="text-zinc-900 font-medium">{formatFullCurrency(v.total)}</span>
                  <span className="text-[10px] text-zinc-400 ml-1.5">{pct.toFixed(1)}%</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

/* ─── Top products table ─── */
const TopProductsTable = ({ products }) => (
  <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-zinc-100">
      <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Top Products by Spend</h2>
    </div>
    <div className="overflow-y-auto max-h-[400px]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100">
            <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Product</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Avg Unit</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => (
            <tr key={p.name} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors">
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-300 w-4">{i + 1}</span>
                  <div className="min-w-0">
                    <span className="block text-zinc-900 truncate max-w-[200px]">{p.name}</span>
                    <span className="block text-[10px] text-zinc-400">{p.category}</span>
                  </div>
                </div>
              </td>
              <td className="px-4 py-2.5 text-right">
                {p.avgUnitCost != null ? (
                  <span className="text-zinc-600">
                    {formatFullCurrency(p.avgUnitCost)}
                    {p.unit && <span className="text-zinc-400">/{p.unit}</span>}
                  </span>
                ) : (
                  <span className="text-zinc-300">--</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right">
                <span className="text-zinc-900 font-medium">{formatFullCurrency(p.total)}</span>
                <span className="text-[10px] text-zinc-400 ml-1">x{p.count}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* ─── Price drift table ─── */
const PriceDriftTable = ({ trends, onViewPdf, activeInvoiceId }) => {
  const [expanded, setExpanded] = useState(null);
  const [unitOverrides, setUnitOverrides] = useState({}); // { productHash: 'lb' }

  // Recompute a trend row's prices when unit is overridden
  const getRowData = (t) => {
    const overrideUnit = unitOverrides[t.product_hash];
    if (!overrideUnit || overrideUnit === t.unit) {
      return { unit: t.unit, oldest: t.oldest_cost, latest: t.latest_cost, changePct: t.change_pct, months: t.months };
    }
    // Re-resolve prices from each month's prices_by_unit
    const months = t.months.map(m => ({
      ...m,
      cost: m.prices_by_unit?.[overrideUnit] ?? m.cost,
    }));
    const oldest = months[0]?.cost;
    const latest = months[months.length - 1]?.cost;
    const changePct = oldest > 0 ? Math.round(((latest - oldest) / oldest) * 10000) / 100 : null;
    return { unit: overrideUnit, oldest, latest, changePct, months };
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <style>{`@keyframes starFlash { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }`}</style>
      <div className="overflow-y-auto max-h-[500px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-zinc-100">
              <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Product</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2 hidden lg:table-cell">Vendor</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Oldest</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Latest</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Change</th>
              <th className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2 hidden xl:table-cell">Trend</th>
            </tr>
          </thead>
          <tbody>
            {trends.map((t) => {
              const row = getRowData(t);
              const isUp = (row.changePct ?? 0) > 0;
              const isDown = (row.changePct ?? 0) < 0;
              const isFlat = row.changePct === 0;
              const absPct = Math.abs(row.changePct ?? 0);
              const isExpanded = expanded === t.name;
              const hasMultipleUnits = (t.available_units || []).length > 1;

              // Sparkline dimensions
              const W = 80, H = 20;
              const costs = row.months.map((m) => m.cost);
              const minC = Math.min(...costs);
              const maxC = Math.max(...costs);
              const range = maxC - minC || 1;
              const points = row.months.map((m, i) => {
                const x = row.months.length === 1 ? W / 2 : (i / (row.months.length - 1)) * W;
                const y = H - ((m.cost - minC) / range) * (H - 4) - 2;
                return `${x},${y}`;
              }).join(' ');

              const isActive = activeInvoiceId && t.months.some((m) => (m.invoice_id || m.document_id || m.bill_id) === activeInvoiceId);

              return (
                <React.Fragment key={t.name}>
                  <tr
                    className={`border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors cursor-pointer ${isActive ? 'bg-amber-50' : ''}`}
                    onClick={() => setExpanded(isExpanded ? null : t.name)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="min-w-0 flex items-center gap-1.5">
                        {isActive && <span style={{ animation: 'starFlash 1.2s ease-in-out infinite', color: '#f59e0b', fontSize: '12px', flexShrink: 0 }}>&#x2733;</span>}
                        <div className="min-w-0">
                          <span className="block text-zinc-900 truncate max-w-[260px]">{t.name}</span>
                          <span className="block text-[10px] text-zinc-400">
                            {t.item_number && <span className="font-mono mr-1.5">#{t.item_number}</span>}
                            {[t.category, t.subcategory].filter(Boolean).join(' › ')}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell">
                      <span className="text-xs text-zinc-500 truncate max-w-[140px] block">{t.vendor}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="text-xs text-zinc-500">{formatFullCurrency(row.oldest)}{row.unit && <span className="text-zinc-300">/{row.unit}</span>}</span>
                      <span className="block text-[10px] text-zinc-300">{t.oldest_month}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="text-xs text-zinc-900 font-medium">{formatFullCurrency(row.latest)}{row.unit && <span className="text-zinc-400 font-normal">/{row.unit}</span>}</span>
                      <span className="block text-[10px] text-zinc-300">{t.latest_month}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {row.changePct != null ? (
                        <span className={`text-xs font-medium tabular-nums ${isUp ? 'text-red-500' : isDown ? 'text-emerald-600' : 'text-zinc-400'}`}>
                          {isUp ? '↑' : isDown ? '↓' : '—'} {isFlat ? '0%' : `${absPct.toFixed(1)}%`}
                        </span>
                      ) : (
                        <span className="text-zinc-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 hidden xl:table-cell">
                      <svg width={W} height={H} className="overflow-visible">
                        <polyline
                          points={points}
                          fill="none"
                          stroke={isUp ? '#ef4444' : isDown ? '#059669' : '#a1a1aa'}
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                        {row.months.map((m, i) => {
                          const x = row.months.length === 1 ? W / 2 : (i / (row.months.length - 1)) * W;
                          const y = H - ((m.cost - minC) / range) * (H - 4) - 2;
                          return <circle key={i} cx={x} cy={y} r="2" fill={isUp ? '#ef4444' : isDown ? '#059669' : '#a1a1aa'} />;
                        })}
                      </svg>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-zinc-100 bg-zinc-50/60">
                      <td colSpan={6} className="px-4 py-3">
                        {/* Unit selector */}
                        {hasMultipleUnits && (
                          <div className="flex items-center gap-1.5 mb-3">
                            <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-medium">Unit:</span>
                            {t.available_units.map(u => (
                              <button
                                key={u}
                                onClick={(e) => { e.stopPropagation(); setUnitOverrides(prev => ({ ...prev, [t.product_hash]: u })); }}
                                className={`px-2 py-0.5 text-[11px] rounded-md border transition-colors ${
                                  (unitOverrides[t.product_hash] || t.unit) === u
                                    ? 'bg-zinc-900 text-white border-zinc-900'
                                    : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400 hover:text-zinc-700'
                                }`}
                              >
                                {u}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2 flex-wrap">
                          {row.months.map((m) => (
                            <button
                              key={m.month}
                              onClick={(e) => { e.stopPropagation(); const id = m.invoice_id || m.document_id || m.bill_id; if (id) onViewPdf(id, m.raw_description); }}
                              className={`text-center px-3 py-1.5 bg-white border rounded-md transition-colors ${(m.invoice_id || m.document_id || m.bill_id) ? 'border-zinc-200 hover:border-zinc-400 hover:shadow-sm cursor-pointer' : 'border-zinc-100 cursor-default opacity-50'}`}
                              title={(m.invoice_id || m.document_id || m.bill_id) ? 'View invoice PDF' : 'No invoice linked'}
                            >
                              <span className="block text-[10px] text-zinc-400">{m.month}</span>
                              <span className="block text-xs font-medium text-zinc-900">{formatFullCurrency(m.cost)}{row.unit && <span className="text-zinc-400 font-normal">/{row.unit}</span>}</span>
                              {(m.invoice_id || m.document_id || m.bill_id) && <span className="block text-[9px] text-zinc-300 mt-0.5">view PDF</span>}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── Section header with step number ─── */
const SpendOverTimeChart = ({ data, formatCurrency, formatFullCurrency }) => {
  const totalData = useMemo(() => {
    return data
      .map(row => {
        const total = Object.entries(row).reduce((sum, [k, v]) => k === 'month' ? sum : sum + v, 0);
        return { month: row.month, total };
      })
      .filter(r => r.total > 0);
  }, [data]);

  if (totalData.length === 0) return null;

  return (
    <>
      <SectionHeader step="03" title="Spend Over Time" description="Monthly spend trend." />
      <div className="bg-white border border-zinc-200 rounded-lg p-4 mt-3">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={totalData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }} barCategoryGap={totalData.length < 6 ? '40%' : '20%'}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: '#a1a1aa' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(m) => {
                const [y, mo] = m.split('-');
                return new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
              }}
            />
            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} width={55} />
            <Tooltip
              formatter={(value) => [formatFullCurrency(value), 'Total Spend']}
              labelFormatter={(m) => {
                const [y, mo] = m.split('-');
                return new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              }}
              contentStyle={{ borderRadius: 6, border: '1px solid #e4e4e7', fontSize: 12 }}
              cursor={{ fill: '#f4f4f5' }}
            />
            <Bar dataKey="total" fill="#3b5bdb" radius={[4, 4, 0, 0]}>
              <LabelList
                dataKey="total"
                position="top"
                formatter={(v) => formatCurrency(v)}
                style={{ fontSize: 10, fill: '#71717a' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

const VENDOR_PIE_RANGES = [
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: 'All', months: null },
];

const VendorSpendPie = ({ data, vendorNames, vendorColorMap, formatFullCurrency }) => {
  const [range, setRange] = useState(null); // null = all

  const pieData = useMemo(() => {
    let filtered = data;
    if (range) {
      const now = new Date();
      const cutoff = new Date(now.getFullYear(), now.getMonth() - range, 1);
      const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`;
      filtered = data.filter(r => r.month >= cutoffStr);
    }
    const totals = {};
    filtered.forEach(row => {
      Object.entries(row).forEach(([k, v]) => {
        if (k === 'month') return;
        totals[k] = (totals[k] || 0) + v;
      });
    });
    return vendorNames
      .map(name => ({ name, value: Math.round((totals[name] || 0) * 100) / 100 }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data, vendorNames, range]);

  const total = pieData.reduce((s, d) => s + d.value, 0);

  return (
    <>
      <div className="flex items-center justify-between">
        <SectionHeader step="04" title="Vendor Spend" description="Spend distribution by vendor." />
        <div className="flex items-center gap-1">
          {VENDOR_PIE_RANGES.map(r => (
            <button
              key={r.label}
              onClick={() => setRange(r.months)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-widest rounded transition-colors ${
                range === r.months ? 'bg-zinc-900 text-white' : 'text-zinc-400 hover:text-zinc-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg mt-3 overflow-hidden">
        {pieData.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-10">No vendor spend data</p>
        ) : (
          <div className="flex flex-col lg:flex-row">
            {/* Pie chart with total in center */}
            <div className="relative flex items-center justify-center py-6 lg:py-8 lg:w-[320px] shrink-0">
              <ResponsiveContainer width={240} height={240}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    innerRadius={72}
                    paddingAngle={1.5}
                    stroke="#fff"
                    strokeWidth={2}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={entry.name} fill={vendorColorMap[entry.name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [formatFullCurrency(value), name]}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', background: '#fff' }}
                    wrapperStyle={{ zIndex: 20, pointerEvents: 'none' }}
                    offset={20}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Center label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[10px] uppercase tracking-widest text-zinc-400">Total</span>
                <span className="text-lg font-semibold text-zinc-900 tabular-nums">{formatFullCurrency(total)}</span>
                <span className="text-[10px] text-zinc-400">{pieData.length} vendor{pieData.length !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Vendor breakdown list */}
            <div className="flex-1 border-t lg:border-t-0 lg:border-l border-zinc-100">
              {pieData.map((entry, i) => {
                const pct = total > 0 ? ((entry.value / total) * 100) : 0;
                return (
                  <div key={entry.name} className={`flex items-center gap-3 px-5 py-3 ${i > 0 ? 'border-t border-zinc-100' : ''} hover:bg-zinc-50/60 transition-colors`}>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: vendorColorMap[entry.name] || COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-zinc-800 truncate flex-1">{entry.name}</span>
                    {/* Inline bar */}
                    <div className="w-24 h-1.5 bg-zinc-100 rounded-full overflow-hidden shrink-0 hidden sm:block">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: vendorColorMap[entry.name] || COLORS[i % COLORS.length] }}
                      />
                    </div>
                    <span className="text-xs text-zinc-400 tabular-nums w-12 text-right shrink-0">{pct.toFixed(1)}%</span>
                    <span className="text-sm font-medium text-zinc-900 tabular-nums shrink-0">{formatFullCurrency(entry.value)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const SectionHeader = ({ step, title, description }) => (
  <div className="flex items-center gap-3">
    <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-medium px-2 py-1 rounded-md border border-zinc-200 bg-white">
      {step}
    </span>
    <div>
      <h2 className="text-lg font-medium text-zinc-900">{title}</h2>
      <p className="text-xs text-zinc-500">{description}</p>
    </div>
  </div>
);

export default SpendAnalyticsView;
